import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { UserData } from '../types';

interface AppContextType {
  user: User | null;
  userData: UserData | null;
  setUserData: React.Dispatch<React.SetStateAction<UserData | null>>;
  loading: boolean;
}

const AppContext = createContext<AppContextType>({
  user: null,
  userData: null,
  setUserData: () => {},
  loading: true,
});

export const useAppContext = () => useContext(AppContext);

export const AppContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            setUserData(userDoc.data() as UserData);
            await updateDoc(doc(db, 'users', firebaseUser.uid), {
              lastSeen: Date.now(),
            });
          }
        } catch (error) {
          console.error('Error loading user data:', error);
        }
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      updateDoc(doc(db, 'users', user.uid), { lastSeen: Date.now() }).catch(
        () => {}
      );
    }, 60_000);
    return () => clearInterval(interval);
  }, [user]);

  return (
    <AppContext.Provider value={{ user, userData, setUserData, loading }}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
